// Pure helpers that let the flyover share wizard serve TWO subjects: a recording (the original 3D
// flyover) and a hike (the Trail Trailer). Kept out of the Vue component so the branching — which
// mutation to call, what its input looks like, where the poll finds its videos — is unit-testable.

import { normalizeStyleUrl, ASPECT_VERTICAL, ASPECT_HORIZONTAL } from './shareActions';

export interface ShareSubject {
  recordingId?: string | null;
  hikeId?: string | null;
}

/** A config opened for a hike (Trail Trailer) rather than a recording (flyover). */
export function isHikeShare(cfg: ShareSubject | null | undefined): boolean {
  return !!cfg?.hikeId && String(cfg.hikeId).trim() !== '';
}

export type GenerateKind = 'hikeTrailer' | 'recordingFlyover';

export interface GenerateRequest {
  kind: GenerateKind;
  input: Record<string, unknown>;
}

/**
 * The generate-mutation input for the chosen subject and format. A trailer has no speed selector —
 * it runs a fixed body duration by design — so speed is only sent on the recording path. The style is
 * normalised the same way on both, so generation and the server's cached-render reuse line up.
 */
export function buildGenerateRequest(
  cfg: ShareSubject,
  format: 'vertical' | 'horizontal',
  mapStyleUrl?: string | null,
): GenerateRequest {
  const aspect = format === 'vertical' ? ASPECT_VERTICAL : ASPECT_HORIZONTAL;
  if (isHikeShare(cfg)) {
    return {
      kind: 'hikeTrailer',
      input: { hikeId: String(cfg.hikeId), aspect, mapStyleUrl: normalizeStyleUrl(mapStyleUrl) },
    };
  }
  return {
    kind: 'recordingFlyover',
    input: {
      recordingId: String(cfg.recordingId ?? ''),
      aspect,
      speed: 1,
      mapStyleUrl: normalizeStyleUrl(mapStyleUrl),
    },
  };
}

/** Pull the poll's video list out of either query's response shape. */
export function videosFromPollData(data: unknown, kind: GenerateKind): unknown[] {
  const d = (data ?? {}) as Record<string, any>;
  const list = kind === 'hikeTrailer' ? d.hikeTrailerVideos : d.recording?.flyoverVideos;
  return Array.isArray(list) ? list : [];
}

export interface HikePageGlobals {
  hike_id?: string | number | null;
  trail_name?: string | null;
  hike_name?: string | null;
}

/**
 * The share-wizard config for a hike's Trail Trailer, built from the hike page's window globals
 * (hikes/show sets window.hike_id and window.trail_name). shareUrl is the page itself — the hike IS
 * the shareable thing. Returns null when the page carries no hike id, so the caller simply doesn't
 * offer the button.
 */
export function buildHikeShareConfig(
  globals: HikePageGlobals | null | undefined,
  pageUrl: string,
  mapStyleUrl?: string | null,
): { hikeId: string; title: string; shareUrl: string; mapStyleUrl?: string } | null {
  const id = globals?.hike_id != null ? String(globals.hike_id).trim() : '';
  if (!id) return null;
  return {
    hikeId: id,
    title: (globals?.hike_name || globals?.trail_name || 'Hike').trim(),
    shareUrl: pageUrl || '',
    ...(mapStyleUrl ? { mapStyleUrl } : {}),
  };
}

/* ---------- Post-save trailer prompt ----------
 * When the planner saves a hike it redirects to /user/hikes while the server is still building the
 * hike's content (processing_status: pending -> processing -> completed). The hikes list already
 * polls for that transition; these helpers carry "prompt the trailer wizard for THIS hike" across
 * the redirect (sessionStorage, same tab only) and decide when the list may actually open it. */

export const PENDING_TRAILER_PROMPT_KEY = 'pendingTrailerPromptHike';

export interface PendingTrailerPrompt {
  hikeId: string;
  title: string;
}

/** The sessionStorage payload for a just-saved hike, or null when there's nothing to remember. */
export function pendingTrailerPromptPayload(
  hikeId: string | number | null | undefined,
  title?: string | null,
): string | null {
  const id = hikeId != null ? String(hikeId).trim() : '';
  if (!id) return null;
  return JSON.stringify({ hikeId: id, title: (title || '').trim() });
}

/** Parse what a previous page stored; malformed or empty values read as "no pending prompt". */
export function parsePendingTrailerPrompt(raw: string | null | undefined): PendingTrailerPrompt | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { hikeId?: unknown; title?: unknown };
    const id = parsed?.hikeId != null ? String(parsed.hikeId).trim() : '';
    if (!id) return null;
    return { hikeId: id, title: typeof parsed.title === 'string' ? parsed.title : '' };
  } catch {
    return null;
  }
}

interface TrailForPrompt {
  id?: string | number | null;
  name?: string | null;
  processingStatus?: string | null;
  shareableUrl?: string | null;
}

/**
 * The share-wizard config for a pending post-save prompt, or null while the prompt must keep
 * waiting. Null means "not yet": the hike hasn't appeared in the list, or is still
 * pending/processing (a legacy null status counts as done, matching the list's own poll). The
 * trailer needs the processed route geometry, so the wizard must not open sooner.
 */
export function trailerPromptConfig(
  pending: PendingTrailerPrompt | null | undefined,
  trails: ReadonlyArray<TrailForPrompt> | null | undefined,
  fallbackUrl: string,
): { hikeId: string; title: string; shareUrl: string } | null {
  if (!pending?.hikeId) return null;
  const trail = (trails || []).find((t) => t?.id != null && String(t.id) === pending.hikeId);
  if (!trail) return null;
  const status = trail.processingStatus;
  if (status === 'pending' || status === 'processing') return null;
  if (status && status !== 'completed') return null; // failed imports never get a trailer prompt
  return {
    hikeId: pending.hikeId,
    title: (trail.name || pending.title || 'Hike').trim(),
    shareUrl: trail.shareableUrl || fallbackUrl || '',
  };
}
